import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { Card, Badge } from '../../common';

const HealthStatus = () => {
  const [health, setHealth]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const isAdmin = (() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user?.type === 'admin' || user?.role === 'admin';
    } catch { return false; }
  })();

  useEffect(() => {
    checkHealth();
    const interval = setInterval(checkHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const checkHealth = async () => {
    try {
      setError(null);
      const response = await fetch('https://bot-api.maralabs.com.br/health');
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setHealth(await response.json());
    } catch (err) {
      console.error('Erro ao verificar saúde da API:', err);
      setError(err.message);
      setHealth({ status: 'error', message: 'Não foi possível conectar', services: {} });
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'healthy' || status === 'ok')
      return <CheckCircle className="w-4 h-4 text-emerald-500" />;
    if (status === 'unhealthy' || status === 'down' || status === 'error')
      return <XCircle className="w-4 h-4 text-red-500" />;
    return <AlertCircle className="w-4 h-4 text-amber-500" />;
  };

  const getStatusText = (status) => {
    if (status === 'ok' || status === 'healthy') return 'Operacional';
    if (status === 'degraded' || status === 'warning') return 'Degradado';
    return 'Fora do ar';
  };

  if (loading && !health) {
    return (
      <Card>
        <Card.Header><Card.Title>Status do Sistema</Card.Title></Card.Header>
        <Card.Body>
          <div className="flex items-center justify-center py-8 gap-2">
            <RefreshCw className="w-4 h-4 text-slate-400 animate-spin" />
            <span className="text-sm text-slate-500 dark:text-slate-400">Verificando...</span>
          </div>
        </Card.Body>
      </Card>
    );
  }

  const overallStatus = health?.status || 'unknown';
  const isHealthy = overallStatus === 'ok' || overallStatus === 'healthy';

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center justify-between">
          <Card.Title>Status do Sistema</Card.Title>
          <button
            onClick={checkHealth}
            disabled={loading}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
            title="Atualizar"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </Card.Header>
      <Card.Body>
        {error ? (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-xl">
            <div className="flex items-center gap-2 mb-1">
              <XCircle className="w-4 h-4 text-red-500" />
              <span className="text-sm font-semibold text-red-800 dark:text-red-300">Erro de Conexão</span>
            </div>
            <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl">
              <div className="flex items-center gap-2.5">
                {getStatusIcon(overallStatus)}
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Status Geral</span>
              </div>
              <Badge variant={isHealthy ? 'success' : 'warning'} dot>
                {getStatusText(overallStatus)}
              </Badge>
            </div>

            {isAdmin && health?.services && Object.keys(health.services).length > 0 && (
              <div className="space-y-2 pt-2 border-t border-slate-100 dark:border-slate-700/60">
                <p className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                  Serviços
                </p>
                {Object.entries(health.services).map(([service, status]) => {
                  const labels = { api: 'API', database: 'Database', redis: 'Redis', evolution: 'Evolution API' };
                  return (
                    <div key={service} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(status)}
                        <span className="text-sm text-slate-600 dark:text-slate-400">
                          {labels[service] || service}
                        </span>
                      </div>
                      <span className={`text-xs font-medium ${
                        status === 'healthy' || status === 'ok'
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : status === 'not_configured'
                          ? 'text-slate-400'
                          : 'text-red-500'
                      }`}>
                        {getStatusText(status)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="pt-2 border-t border-slate-100 dark:border-slate-700/60">
              <p className="text-[11px] text-slate-400 dark:text-slate-500 text-center">
                Atualizado às{' '}
                {health?.timestamp
                  ? new Date(health.timestamp).toLocaleTimeString('pt-BR')
                  : new Date().toLocaleTimeString('pt-BR')}
              </p>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default HealthStatus;
